import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { UserStore } from '../models/internal/user.store';
import { chaveTool, Tool } from '../models/internal/tool-model';

/**
 * Os até 4 atalhos que o usuário fixa no topo do menu.
 *
 * Guarda só a **chave** de cada item, nunca o objeto: quem resolve a chave contra o menu
 * vivo é o MenuComponent, e é isso que faz a permissão vencer a preferência de graça —
 * um item que o usuário não pode mais acessar simplesmente não é encontrado.
 *
 * Mesmo desenho do [SearchHistoryService]: localStorage porque precisa sobreviver à
 * sessão, e a chave leva o e-mail porque a máquina do balcão é de mais de um operador.
 */
@Injectable({ providedIn: 'root' })
export class MenuFavoritesService {
    private static readonly CHAVE = 'MENU_FAVORITES';
    public static readonly LIMITE = 4;

    private readonly userStore = inject(UserStore);

    private readonly _favoritos = signal<string[]>([]);
    public readonly favoritos = this._favoritos.asReadonly();

    private readonly email = computed(() => this.userStore.user().email);

    /**
     * O e-mail chega depois, do auth0. Até lá não há de quem é o favorito: ler ou gravar
     * numa chave sem e-mail seria usar um balde que todos os operadores do balcão
     * compartilham, e o que fosse fixado nesse intervalo sumiria quando o e-mail chegasse.
     */
    public readonly pronto = computed(() => !!this.email());

    constructor() {
        // Só o e-mail dispara a releitura; mudar plano ou CNPJ não troca de balde.
        effect(() => {
            this._favoritos.set(this.pronto() ? this.ler() : []);
        });
    }

    public estaFavoritado(item: Tool): boolean {
        return this._favoritos().includes(chaveTool(item));
    }

    /**
     * Fixa ou desafixa. Retorna false quando o limite impediu de fixar. Sem e-mail não faz
     * nada — a estrela nem aparece nesse intervalo — e não há limite a avisar.
     */
    public alternar(item: Tool): boolean {
        if (!this.pronto()) {
            return true;
        }

        const chave = chaveTool(item);
        const atuais = this._favoritos();

        if (atuais.includes(chave)) {
            this.gravar(atuais.filter(c => c !== chave));
            return true;
        }

        if (atuais.length >= MenuFavoritesService.LIMITE) {
            return false;
        }

        this.gravar([...atuais, chave]);
        return true;
    }

    private gravar(chaves: string[]): void {
        this._favoritos.set(chaves);

        try {
            localStorage.setItem(this.chave(), JSON.stringify(chaves));
        } catch {
            // Cota estourada ou navegação privada: os favoritos valem só nesta sessão.
            // Não é motivo para derrubar o menu.
        }
    }

    private chave(): string {
        return `${MenuFavoritesService.CHAVE}:${this.email()}`;
    }

    private ler(): string[] {
        try {
            const cru = localStorage.getItem(this.chave());
            const dados: unknown = cru ? JSON.parse(cru) : [];

            // O que vem do disco é entrada não confiável: pode ter sido editado à mão ou
            // ter ficado de uma versão anterior, com outro formato.
            return Array.isArray(dados)
                ? dados.filter((c): c is string => typeof c === 'string' && !!c).slice(0, MenuFavoritesService.LIMITE)
                : [];
        } catch {
            return [];
        }
    }
}
